import type { ChatStreamEvent } from '@volt/shared';

/**
 * Server-Sent Events parsing for the chat stream.
 *
 * The chat endpoint is a POST returning `text/event-stream`, so `EventSource`
 * cannot be used. Decoded text chunks are fed into {@link SSELineParser}, a
 * small, dependency-free, fully-testable state machine that turns a
 * byte/character stream into discrete SSE events following the WHATWG
 * event-stream grammar (only `\n` and `\r\n` line terminators, which is what
 * the API emits).
 *
 * The actual transport is {@link XHRChatStream} (see below), not `fetch` +
 * `ReadableStream` — see its doc comment for why.
 */

export interface SSEEvent {
  event?: string;
  data: string;
  id?: string;
}

export class SSELineParser {
  private buffer = '';
  private dataLines: string[] = [];
  private eventName: string | undefined;
  private lastId: string | undefined;
  private hasFields = false;

  /** Feed a chunk of text; returns every event completed by a blank line. */
  push(chunk: string): SSEEvent[] {
    this.buffer += chunk;
    const events: SSEEvent[] = [];
    let newlineIndex = this.buffer.indexOf('\n');
    while (newlineIndex !== -1) {
      let line = this.buffer.slice(0, newlineIndex);
      this.buffer = this.buffer.slice(newlineIndex + 1);
      if (line.endsWith('\r')) line = line.slice(0, -1);
      const event = this.consumeLine(line);
      if (event) events.push(event);
      newlineIndex = this.buffer.indexOf('\n');
    }
    return events;
  }

  /**
   * Flush any trailing content once the stream ends. A well-formed stream ends
   * each event with a blank line; if a final line arrived without one we still
   * dispatch it so nothing is silently dropped.
   */
  flush(): SSEEvent[] {
    const events: SSEEvent[] = [];
    if (this.buffer.length > 0) {
      let line = this.buffer;
      this.buffer = '';
      if (line.endsWith('\r')) line = line.slice(0, -1);
      const event = this.consumeLine(line);
      if (event) events.push(event);
    }
    const pending = this.dispatch();
    if (pending) events.push(pending);
    return events;
  }

  private consumeLine(line: string): SSEEvent | null {
    if (line === '') return this.dispatch();
    if (line.startsWith(':')) return null; // comment / keep-alive
    this.hasFields = true;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'data':
        this.dataLines.push(value);
        break;
      case 'event':
        this.eventName = value;
        break;
      case 'id':
        this.lastId = value;
        break;
      default:
        break;
    }
    return null;
  }

  private dispatch(): SSEEvent | null {
    if (!this.hasFields) return null;
    const event: SSEEvent = { data: this.dataLines.join('\n') };
    if (this.eventName !== undefined) event.event = this.eventName;
    if (this.lastId !== undefined) event.id = this.lastId;
    this.dataLines = [];
    this.eventName = undefined;
    this.hasFields = false;
    return event;
  }
}

/** Convenience for tests / one-shot parsing: parse a whole string at once. */
export function parseSSE(text: string): SSEEvent[] {
  const parser = new SSELineParser();
  return [...parser.push(text), ...parser.flush()];
}

/** Parse one SSE `data` payload into a typed chat event, or null if unusable. */
export function parseChatEvent(data: string): ChatStreamEvent | null {
  const trimmed = data.trim();
  if (trimmed === '' || trimmed === '[DONE]') return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && typeof (parsed as { type?: unknown }).type === 'string') {
      return parsed as ChatStreamEvent;
    }
    return null;
  } catch {
    console.warn('[VOLT-DEBUG] Failed to parse SSE chat event', trimmed);
    return null;
  }
}

/** Read a fetch response body as a stream of typed {@link ChatStreamEvent}s. */
export async function* readChatStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SSELineParser();
  try {
    for (;;) {
      if (signal?.aborted) return;
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const event of parser.push(chunk)) {
        const parsed = parseChatEvent(event.data);
        if (parsed) yield parsed;
      }
    }
    for (const event of parser.flush()) {
      const parsed = parseChatEvent(event.data);
      if (parsed) yield parsed;
    }
  } finally {
    reader.releaseLock();
  }
}

export interface XHRStreamOptions {
  method: string;
  headers: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}

const READY_STATE_NAMES: Record<number, string> = {
  0: 'UNSENT',
  1: 'OPENED',
  2: 'HEADERS_RECEIVED',
  3: 'LOADING',
  4: 'DONE',
};

let xhrDebugCounter = 0;

/**
 * Streams a `text/event-stream` HTTP response using `XMLHttpRequest` instead
 * of `fetch` + `ReadableStream`.
 *
 * Safari/WebKit — including the Telegram in-app browser, which is what the
 * Mini App actually runs in on iOS — does not reliably support incremental
 * `fetch` response streaming. In practice this surfaced as `reader.read()`
 * throwing `TypeError: Load failed` partway through the response, or the
 * whole body only becoming available once the connection closed (which is
 * why a reply would "appear" after leaving and reopening the chat, but was
 * never typed out live). `XMLHttpRequest` with `responseType: 'text'`,
 * polled via the `readyState === LOADING` progress state, delivers partial
 * text reliably across engines and is the traditional workaround for
 * streaming SSE-like responses in WebKit.
 *
 * TEMP DEBUG: this class currently logs every readyState transition, byte
 * count, and error verbosely under the `[VOLT-XHR#n]` tag so we can diagnose
 * why replies sometimes only appear after leaving/reopening the chat. Remove
 * once the root cause is confirmed fixed.
 */
export class XHRChatStream implements AsyncIterable<SSEEvent> {
  /** Resolves as soon as response headers arrive, with the HTTP status code. */
  readonly status: Promise<number>;
  /** Resolves with the full response text once the request finishes (any status). */
  readonly text: Promise<string>;

  private readonly xhr = new XMLHttpRequest();
  private readonly parser = new SSELineParser();
  private readonly queue: SSEEvent[] = [];
  private readIndex = 0;
  private finished = false;
  private error: Error | null = null;
  private waiter: (() => void) | null = null;
  private readonly debugId = ++xhrDebugCounter;
  private readonly debugStart = Date.now();

  private debugLog(msg: string, extra?: Record<string, unknown>): void {
    if (import.meta.env.DEV) {
      const elapsedMs = Date.now() - this.debugStart;
      console.warn(`[VOLT-XHR#${this.debugId}] +${elapsedMs}ms ${msg}`, extra ?? '');
    }
  }

  constructor(url: string, options: XHRStreamOptions) {
    const xhr = this.xhr;
    let resolveStatus!: (status: number) => void;
    let resolveText!: (text: string) => void;
    this.status = new Promise((resolve) => {
      resolveStatus = resolve;
    });
    this.text = new Promise((resolve) => {
      resolveText = resolve;
    });
    let statusSent = false;
    const sendStatus = () => {
      if (statusSent) return;
      statusSent = true;
      this.debugLog('resolving status promise', { status: xhr.status });
      resolveStatus(xhr.status);
    };

    this.debugLog('opening XHR', { url, method: options.method });
    xhr.open(options.method, url, true);
    for (const [key, value] of Object.entries(options.headers)) {
      xhr.setRequestHeader(key, value);
    }

    xhr.onreadystatechange = () => {
      this.debugLog('onreadystatechange', {
        readyState: xhr.readyState,
        readyStateName: READY_STATE_NAMES[xhr.readyState],
        status: xhr.status,
        responseTextLength: safeResponseTextLength(xhr),
      });
      if (xhr.readyState >= XMLHttpRequest.HEADERS_RECEIVED && xhr.status !== 0) {
        sendStatus();
      }
      if (xhr.readyState === XMLHttpRequest.LOADING) {
        this.pump();
        this.notify();
      }
      if (xhr.readyState === XMLHttpRequest.DONE) {
        sendStatus();
        this.pump();
        const flushed = this.parser.flush();
        this.debugLog('DONE: flushed trailing events', { count: flushed.length });
        this.queue.push(...flushed);
        this.finished = true;
        resolveText(xhr.responseText);
        this.notify();
      }
    };
    xhr.onerror = (event) => {
      this.debugLog('onerror fired', {
        readyState: xhr.readyState,
        status: xhr.status,
        responseTextLength: safeResponseTextLength(xhr),
        event: String(event),
      });
      sendStatus();
      this.error = new Error('Network error');
      this.finished = true;
      resolveText(xhr.responseText ?? '');
      this.notify();
    };
    xhr.ontimeout = () => {
      this.debugLog('ontimeout fired', { readyState: xhr.readyState });
    };
    xhr.onabort = () => {
      this.debugLog('onabort fired', { readyState: xhr.readyState });
      sendStatus();
      if (!this.finished) {
        this.finished = true;
        resolveText(xhr.responseText ?? '');
        this.notify();
      }
    };
    xhr.onloadend = () => {
      this.debugLog('onloadend fired', {
        readyState: xhr.readyState,
        status: xhr.status,
        responseTextLength: safeResponseTextLength(xhr),
      });
    };

    if (options.signal) {
      if (options.signal.aborted) {
        this.debugLog('signal already aborted before send — aborting immediately');
        xhr.abort();
      } else {
        options.signal.addEventListener(
          'abort',
          () => {
            this.debugLog('abort signal received — aborting XHR');
            xhr.abort();
          },
          { once: true },
        );
      }
    }

    this.debugLog('sending XHR', { bodyLength: options.body?.length ?? 0 });
    xhr.send(options.body ?? null);
  }

  abort(): void {
    this.debugLog('abort() called externally');
    this.xhr.abort();
  }

  private pump(): void {
    const text = this.xhr.responseText;
    if (text.length > this.readIndex) {
      const chunk = text.slice(this.readIndex);
      this.debugLog('pump: new bytes available', {
        previousIndex: this.readIndex,
        newLength: text.length,
        chunkPreview: chunk.slice(0, 200),
      });
      this.readIndex = text.length;
      const events = this.parser.push(chunk);
      if (events.length > 0) {
        this.debugLog('pump: parsed SSE events', {
          count: events.length,
          dataPreviews: events.map((e) => e.data.slice(0, 120)),
        });
      }
      this.queue.push(...events);
    }
  }

  private notify(): void {
    if (this.waiter) {
      const resolve = this.waiter;
      this.waiter = null;
      resolve();
    }
  }

  async *[Symbol.asyncIterator](): AsyncGenerator<SSEEvent> {
    this.debugLog('async iterator started');
    for (;;) {
      if (this.queue.length > 0) {
        const event = this.queue.shift()!;
        this.debugLog('yielding queued event', { dataPreview: event.data.slice(0, 120) });
        yield event;
        continue;
      }
      if (this.finished) {
        if (this.error) {
          this.debugLog('iterator throwing stored error', { message: this.error.message });
          throw this.error;
        }
        this.debugLog('iterator finished cleanly, no more events');
        return;
      }
      this.debugLog('iterator waiting for more data...');
      await new Promise<void>((resolve) => {
        this.waiter = resolve;
      });
    }
  }
}

function safeResponseTextLength(xhr: XMLHttpRequest): number | string {
  try {
    return xhr.responseText.length;
  } catch (error) {
    return `<unreadable: ${(error as Error).message}>`;
  }
}
